import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { money } from "@/lib/api";
import { useRealtime } from "@/context/RealtimeContext";
import { Overline } from "@/components/ui-bits";
import { toast } from "sonner";
import { ShieldCheck, Link2, Rocket, Wallet, Check, Ban, Loader2, Bot, Sparkles, Lock } from "lucide-react";

export default function Approvals() {
  const { user } = useOutletContext() || {};
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [sel, setSel] = useState(new Set());

  const toggle = (key) => setSel((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });

  const load = () => api.get("/approvals").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); const i = setInterval(load, 10000); return () => clearInterval(i); }, []);
  useRealtime(() => load());

  const act = async (key, fn, okMsg) => {
    setBusy(key);
    try { await fn(); toast.success(okMsg); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setBusy(null); }
  };

  const resolveLink = (c, approve) => act(`link-${c.conversation_id}`, () => api.post(`/conversations/${c.conversation_id}/payment-link/resolve`, { approve }), approve ? "Payment link sent" : "Proposal rejected");
  const resolveDeploy = (d, approve) => act(`dep-${d.id}`, () => api.post(`/deployments/${d.id}/approve`, { approve }), approve ? "Deploy approved" : "Deploy rejected");
  const resolveSpend = (s, approve) => act(`spend-${s.id}`, () => api.post(`/spends/${s.id}/resolve`, { approve }), approve ? `Approved ${money(s.amount)}` : `Rejected ${money(s.amount)}`);

  const approveAll = async () => {
    if (!window.confirm(`Approve ALL ${total} pending item(s) — payment links, production deploys and spends — in one go?`)) return;
    setBusy("all");
    try {
      const { data: r } = await api.post("/approvals/approve-all");
      toast.success(`Approved ${r.total} item(s)`, { description: r.errors?.length ? `${r.errors.length} skipped` : "Queue cleared" });
      setSel(new Set()); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk approve failed"); }
    finally { setBusy(null); }
  };

  const approveSelected = async () => {
    const payload = {
      payment_link_ids: [...sel].filter((k) => k.startsWith("link:")).map((k) => k.slice(5)),
      deploy_ids: [...sel].filter((k) => k.startsWith("dep:")).map((k) => k.slice(4)),
      spend_ids: [...sel].filter((k) => k.startsWith("spend:")).map((k) => k.slice(6)),
    };
    setBusy("sel");
    try {
      const { data: r } = await api.post("/approvals/approve-all", payload);
      toast.success(`Approved ${r.total} selected item(s)`, { description: r.errors?.length ? `${r.errors.length} skipped` : undefined });
      setSel(new Set()); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
    finally { setBusy(null); }
  };

  const total = data?.total ?? 0;

  return (
    <div className="space-y-6" data-testid="approvals-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <Overline>Human-in-the-loop</Overline>
          <h1 className="font-display text-3xl font-extrabold tracking-tight mt-1">Approval Queue</h1>
          <p className="text-sm text-slate-400 mt-1">Everything awaiting your sign-off across the workspace — payment links, production deploys and spends — in one place.</p>
        </div>
        {isAdmin && total > 0 && (
          <div className="flex gap-2">
            {sel.size > 0 && (
              <button data-testid="approve-selected-btn" onClick={approveSelected} disabled={busy === "sel"}
                className="flex items-center gap-2 px-4 py-2.5 rounded-md border border-cyan-500/40 bg-cyan-500/10 text-cyan-400 font-bold text-sm hover:bg-cyan-500/20 transition-colors disabled:opacity-60">
                {busy === "sel" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Approve selected ({sel.size})
              </button>
            )}
            <button data-testid="approve-all-btn" onClick={approveAll} disabled={busy === "all"}
              className="flex items-center gap-2 px-4 py-2.5 rounded-md bg-emerald-500 text-[#080B11] font-bold text-sm hover:bg-emerald-400 transition-colors disabled:opacity-60">
              {busy === "all" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Approve all ({total})
            </button>
          </div>
        )}
      </div>

      {!data ? (
        <div className="hud-card rounded-lg p-16 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>
      ) : total === 0 ? (
        <div className="hud-card rounded-lg p-12 text-center" data-testid="approvals-empty">
          <ShieldCheck className="w-10 h-10 text-emerald-500/60 mx-auto" />
          <p className="mt-3 text-slate-300 font-medium">You're all caught up</p>
          <p className="text-sm text-slate-500 mt-1">No payment links, deploys or spends are waiting for approval.</p>
        </div>
      ) : (
        <>
          {!isAdmin && (
            <div className="flex items-center gap-2 text-[11px] font-mono text-slate-500 hud-card rounded-md px-3 py-2"><Lock className="w-3 h-3" /> Only admins can approve or reject these items.</div>
          )}

          <Section icon={Link2} title="Payment Links" count={data.payment_links.length} accent="text-cyan-400">
            {data.payment_links.map((c) => (
              <Row key={c.conversation_id} testid={`approval-link-${c.conversation_id}`}
                left={<>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-100">{c.product_name}</span>
                    <span className="font-mono text-emerald-400 text-sm">{money(c.amount)}</span>
                    {c.auto && <span className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-wider text-violet-400 bg-violet-500/10 border border-violet-500/30 rounded px-1.5 py-0.5"><Sparkles className="w-2.5 h-2.5" /> Auto</span>}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5"><Bot className="w-3 h-3 inline mr-1" />{c.agent_name} → {c.customer_name}</div>
                </>}
                busy={busy === `link-${c.conversation_id}`} disabled={!isAdmin}
                checked={sel.has(`link:${c.conversation_id}`)} onToggle={() => toggle(`link:${c.conversation_id}`)}
                onApprove={() => resolveLink(c, true)} onReject={() => resolveLink(c, false)} approveLabel="Approve & Send" />
            ))}
          </Section>

          <Section icon={Rocket} title="Production Deploys" count={data.deploys.length} accent="text-amber-400">
            {data.deploys.map((d) => (
              <Row key={d.id} testid={`approval-deploy-${d.id}`}
                left={<>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-100">{d.product_name}</span>
                    <span className="font-mono text-[9px] uppercase tracking-wider text-slate-500">v{d.pending_version}</span>
                    {d.source === "auto_delivery" && <span className="font-mono text-[9px] uppercase tracking-wider text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded px-1.5 py-0.5">Auto-delivery</span>}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5"><Bot className="w-3 h-3 inline mr-1" />{d.agent_name} · ships to a live domain</div>
                </>}
                busy={busy === `dep-${d.id}`} disabled={!isAdmin}
                checked={sel.has(`dep:${d.id}`)} onToggle={() => toggle(`dep:${d.id}`)}
                onApprove={() => resolveDeploy(d, true)} onReject={() => resolveDeploy(d, false)} approveLabel="Approve & Ship" />
            ))}
          </Section>

          <Section icon={Wallet} title="Spends" count={data.spends.length} accent="text-emerald-400">
            {data.spends.map((s) => (
              <Row key={s.id} testid={`approval-spend-${s.id}`}
                left={<>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-100">{s.category}</span>
                    <span className="font-mono text-amber-400 text-sm">{money(s.amount)}</span>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5"><Bot className="w-3 h-3 inline mr-1" />{s.agent_name} · {s.description}</div>
                </>}
                busy={busy === `spend-${s.id}`} disabled={!isAdmin}
                checked={sel.has(`spend:${s.id}`)} onToggle={() => toggle(`spend:${s.id}`)}
                onApprove={() => resolveSpend(s, true)} onReject={() => resolveSpend(s, false)} approveLabel="Approve" />
            ))}
          </Section>
        </>
      )}
    </div>
  );
}

function Section({ icon: Icon, title, count, accent, children }) {
  if (count === 0) return null;
  return (
    <div className="hud-card rounded-lg overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-800">
        <Icon className={`w-4 h-4 ${accent}`} />
        <span className="font-mono text-[11px] uppercase tracking-wider text-slate-300">{title}</span>
        <span className="font-mono text-[10px] text-slate-500">· {count}</span>
      </div>
      <div className="divide-y divide-slate-800/60">{children}</div>
    </div>
  );
}

function Row({ left, onApprove, onReject, busy, disabled, approveLabel, testid, checked, onToggle }) {
  return (
    <div className="flex items-center justify-between gap-4 px-5 py-3.5" data-testid={testid}>
      <div className="flex items-center gap-3 min-w-0">
        {!disabled && onToggle && (
          <input type="checkbox" checked={checked} onChange={onToggle} data-testid={`${testid}-check`} className="h-4 w-4 accent-cyan-500 shrink-0" />
        )}
        <div className="min-w-0">{left}</div>
      </div>
      {!disabled && (
        <div className="flex gap-2 shrink-0">
          <button data-testid={`${testid}-approve`} onClick={onApprove} disabled={busy}
            className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-400 font-mono text-[10px] uppercase tracking-wider hover:bg-emerald-500/25 transition-colors disabled:opacity-60">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} {approveLabel}
          </button>
          <button data-testid={`${testid}-reject`} onClick={onReject} disabled={busy}
            className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-red-500/15 border border-red-500/40 text-red-400 font-mono text-[10px] uppercase tracking-wider hover:bg-red-500/25 transition-colors disabled:opacity-60">
            <Ban className="w-3 h-3" /> Reject
          </button>
        </div>
      )}
    </div>
  );
}
